import { Component } from '@theme/component';
import { ThemeEvents, CartUpdateEvent } from '@theme/events';

/**
 * @typedef {object} RiserConfiguratorRefs
 * @property {HTMLElement} [estimateTotal] - Running total value.
 * @property {HTMLElement} [estimateBreakdown] - Running total breakdown line.
 * @property {HTMLElement} [notesCount] - Character counter for the notes field.
 */

/**
 * Drives the furniture riser configurator: conditional fields, the running
 * total, and charging surcharges by adding a $1 fee product to the cart.
 *
 * Surcharges live on `data-riser-fee` attributes (in dollars) so the merchant
 * can change every price from the theme editor without touching code.
 *
 * @extends Component<RiserConfiguratorRefs>
 */
class RiserConfigurator extends Component {
  /** @type {HTMLFormElement | null} */
  #form = null;
  /** Fee quantity waiting to be added once the main item lands in the cart. */
  #pendingFeeQty = 0;
  /** What the pending fee covers, for the cart line. */
  #pendingFeeCovers = '';
  /**
   * One-time token stamped as a hidden property on both the main item and the
   * fee line for a single add-to-cart, so the cart can later tell which fee
   * line pays for which riser line — they're otherwise two unrelated lines.
   */
  #pendingRiserGroup = '';
  /** Guards against reacting to the cart update our own fee add triggers. */
  #applyingFee = false;
  /** The variant and quantity just added, so they can be rolled back. */
  #pendingMainVariantId = null;
  #pendingMainQty = 0;
  /** Unit price of the base variant, in cents. */
  #basePriceCents = 0;
  /** The real Size fieldset the split cards drive. */
  #sizeFieldset = null;
  /** The real Shape fieldset the illustrated cards drive. */
  #shapeFieldset = null;
  /** The mobile running-total bar, when the summary block renders one. */
  #mobileBar = null;
  /** Watches the side panel and the real button so the bar only fills a gap. */
  #barObserver = null;
  /** Set once the configurator has been scrolled to; the bar waits for it. */
  #barArmed = false;
  /** What the observer last decided, before the keyboard gets a say. */
  #barWanted = false;
  /** A text field is focused, so a software keyboard is probably up. */
  #fieldFocused = false;
  /** The visual viewport shrank, which is the other tell for a keyboard. */
  #viewportShrunk = false;

  connectedCallback() {
    super.connectedCallback();

    this.#basePriceCents = Number(this.dataset.basePrice ?? 0) || 0;

    const formId = this.dataset.formId;
    this.#form = formId ? /** @type {HTMLFormElement | null} */ (document.getElementById(formId)) : null;

    this.addEventListener('change', this.#onChange);
    this.addEventListener('input', this.#onInput);
    this.addEventListener('focusin', this.#onFieldFocusIn);
    this.addEventListener('focusout', this.#onFieldFocusOut);
    window.visualViewport?.addEventListener('resize', this.#onViewportResize);

    this.#form?.addEventListener('submit', this.#onSubmit, { capture: true });
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
    document.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);

    // Stamp the form with a fresh correlation token here, on the real submit
    // button's click, rather than inside #onSubmit: product-form.js has its own
    // 'submit' listener on this same <form>, and when two listeners share a
    // target, whichever was *registered* first wins regardless of the capture
    // flag — there's no reliable way to guarantee this one runs first from
    // inside 'submit' itself. A click on the button always finishes running,
    // and finishes mutating the form, before the browser gets to dispatching
    // 'submit' at all, so this has no such race.
    const submitButton = this.#form?.querySelector('button[type="submit"], [name="add"]');
    submitButton?.addEventListener('click', () => this.#tagFormWithRiserGroup());

    this.#setupSizeSplit();
    this.#setupShapeDriver();
    this.#setupMobileBar();
    this.#setupDropzone();
    this.#updateEstimate();
  }

  /**
   * Wires the photo dropzone so a dragged-and-dropped file lands on the real
   * (visually hidden) file input exactly as a browsed-for one would, and
   * toggles a data attribute for the drag-over styling.
   */
  #setupDropzone() {
    const zone = this.querySelector('[data-riser-dropzone]');
    const input = zone?.querySelector('input[type="file"]');
    if (!(zone instanceof HTMLElement) || !(input instanceof HTMLInputElement)) return;

    let dragDepth = 0;

    zone.addEventListener('dragenter', (event) => {
      event.preventDefault();
      dragDepth += 1;
      zone.setAttribute('data-riser-dragover', '');
    });

    zone.addEventListener('dragover', (event) => {
      // Without this, the browser's default is to refuse the drop entirely.
      event.preventDefault();
    });

    zone.addEventListener('dragleave', () => {
      dragDepth = Math.max(0, dragDepth - 1);
      if (dragDepth === 0) zone.removeAttribute('data-riser-dragover');
    });

    zone.addEventListener('drop', (event) => {
      event.preventDefault();
      dragDepth = 0;
      zone.removeAttribute('data-riser-dragover');

      const files = event.dataTransfer?.files;
      if (!files || files.length === 0) return;

      // Assigning to input.files directly isn't allowed; a DataTransfer is
      // the one way to hand a FileList to a real <input type="file">.
      input.files = files;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();

    this.#form?.removeEventListener('submit', this.#onSubmit, { capture: true });
    document.removeEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
    document.removeEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);
    document.removeEventListener(ThemeEvents.variantUpdate, this.#syncSplitFromVariant);
    document.removeEventListener(ThemeEvents.variantUpdate, this.#syncShapeFromVariant);

    window.visualViewport?.removeEventListener('resize', this.#onViewportResize);
    this.#barObserver?.disconnect();
    this.#barObserver = null;
    document.body.classList.remove('riser-mobile-total-open');
    // It was moved onto the body, so it will not go with the block's markup.
    if (this.#mobileBar?.parentElement === document.body) this.#mobileBar.remove();
    this.#mobileBar = null;
  }

  /**
   * The mobile bar fills the stretch where neither the summary panel nor the
   * real Add to cart is on screen. While either is visible it stays down, so
   * the total is never shown twice.
   */
  #setupMobileBar() {
    const bar = document.querySelector('[data-riser-mobile-bar]');
    if (!(bar instanceof HTMLElement)) return;

    // A fixed element anchors to the nearest transformed ancestor, and the
    // block renders several layout panels deep. Re-home it on the body so it
    // pins to the viewport whatever the section wraps it in.
    if (bar.parentElement !== document.body) {
      for (const stale of document.querySelectorAll('body > [data-riser-mobile-bar]')) stale.remove();
      document.body.append(bar);
    }
    this.#mobileBar = bar;

    const toggle = bar.querySelector('[data-riser-mobile-toggle]');
    const detail = bar.querySelector('.riser-mobile-total__detail');
    if (toggle instanceof HTMLElement && detail instanceof HTMLElement) {
      toggle.addEventListener('click', () => {
        const open = toggle.getAttribute('aria-expanded') === 'true';
        toggle.setAttribute('aria-expanded', String(!open));
        detail.hidden = open;
        this.#measureBar();
      });
    }

    // Proxy the real button so the browser still reports and scrolls to the
    // first missing field rather than silently doing nothing.
    bar.querySelector('[data-riser-mobile-add]')?.addEventListener('click', () => {
      const real = this.#form?.querySelector('button[type="submit"], [name="add"]');
      if (real instanceof HTMLElement) real.click();
    });

    const panel = document.querySelector('.riser-summary-panel');
    const realButton = this.#form?.querySelector('button[type="submit"], [name="add"]');
    const watched = [panel, realButton, this].filter((el) => el instanceof Element);
    if (typeof IntersectionObserver === 'undefined') return;

    const onScreen = new Set();
    this.#barObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          // Nothing to show before the customer has reached the configurator;
          // the hero and use cases do not need a total over them.
          if (entry.target === this) {
            if (entry.isIntersecting) this.#barArmed = true;
            continue;
          }
          if (entry.isIntersecting) onScreen.add(entry.target);
          else onScreen.delete(entry.target);
        }
        this.#barWanted = this.#barArmed && onScreen.size === 0;
        this.#applyBar();
      },
      { rootMargin: '0px 0px -10% 0px' }
    );
    for (const el of watched) this.#barObserver.observe(el);
  }

  /** Text fields that bring up a keyboard. Radios and checkboxes do not. */
  static #TYPING_FIELDS =
    'textarea, select, input:not([type="radio"]):not([type="checkbox"]):not([type="file"]):not([type="hidden"])';

  #onFieldFocusIn = (event) => {
    if (!(event.target instanceof HTMLElement)) return;
    if (!event.target.matches(RiserConfigurator.#TYPING_FIELDS)) return;
    this.#fieldFocused = true;
    this.#applyBar();
  };

  #onFieldFocusOut = () => {
    // Tabbing between fields fires focusout before the next focusin, so let
    // the move settle before deciding the keyboard has gone.
    setTimeout(() => {
      const active = document.activeElement;
      this.#fieldFocused =
        active instanceof HTMLElement &&
        this.contains(active) &&
        active.matches(RiserConfigurator.#TYPING_FIELDS);
      this.#applyBar();
    }, 80);
  };

  #onViewportResize = () => {
    const vv = window.visualViewport;
    if (!vv) return;
    // A keyboard takes a good slice of the screen; a URL bar collapsing does not.
    this.#viewportShrunk = vv.height < window.innerHeight * 0.75;
    this.#applyBar();
  };

  /**
   * The bar is fixed to the viewport, so a software keyboard either covers it
   * or shoves it over the field being typed into. Stand it down while one is
   * up and bring it back on blur.
   */
  #applyBar() {
    const keyboardUp = this.#fieldFocused || this.#viewportShrunk;
    this.#toggleBar(this.#barWanted && !keyboardUp);
  }

  /** @param {boolean} show */
  #toggleBar(show) {
    const bar = this.#mobileBar;
    if (!bar) return;
    if (show) {
      bar.hidden = false;
      // Let the element lay out before transitioning it in.
      requestAnimationFrame(() => {
        bar.classList.add('riser-mobile-total--shown');
        this.#measureBar();
      });
    } else {
      bar.classList.remove('riser-mobile-total--shown');
      document.body.classList.remove('riser-mobile-total-open');
    }
  }

  /** Keeps the page's bottom padding matched to the bar's real height. */
  #measureBar() {
    const bar = this.#mobileBar;
    if (!bar || !bar.classList.contains('riser-mobile-total--shown')) return;
    const height = bar.getBoundingClientRect().height;
    document.body.style.setProperty('--riser-mobile-total-height', `${Math.round(height)}px`);
    document.body.classList.add('riser-mobile-total-open');
  }


  /**
   * The Size variant option bundles base and lift into one long list. The two
   * card groups above stand in for it: picking from them checks the matching
   * native radio, and the real fieldset is hidden.
   */
  #setupSizeSplit() {
    const optionName = this.dataset.splitSizeOption;
    if (!optionName) return;

    const fieldset = this.#findOptionFieldset(optionName);
    if (!fieldset) return;

    this.#sizeFieldset = fieldset;
    fieldset.style.display = 'none';

    // Reflect whatever variant the page loaded with, including ?variant= links.
    this.#syncSplitFromVariant();
    document.addEventListener(ThemeEvents.variantUpdate, this.#syncSplitFromVariant);
  }

  /**
   * Splits a real Size option value into its base and lift parts.
   * The theme editor strips spaces from text settings, so the separator and
   * suffix are compared trimmed and the parts are trimmed on the way out.
   */
  #parseSizeValue(value) {
    const separator = (this.dataset.sizeSeparator ?? '').trim();
    if (!separator) return null;

    const index = value.indexOf(separator);
    if (index === -1) return null;

    const suffix = (this.dataset.sizeSuffix ?? '').trim();
    let lift = value.slice(index + separator.length).trim();
    if (suffix && lift.endsWith(suffix)) lift = lift.slice(0, -suffix.length).trim();

    return { base: value.slice(0, index).trim(), lift };
  }

  /** Pushes the split selection onto the real variant option. */
  #applySizeSplit() {
    const fieldset = this.#sizeFieldset;
    if (!fieldset) return;

    const base = this.querySelector('[data-riser-size-part="base"]:checked');
    const lift = this.querySelector('[data-riser-size-part="lift"]:checked');
    if (!(base instanceof HTMLInputElement) || !(lift instanceof HTMLInputElement)) return;

    // Match against the real option values instead of rebuilding the string,
    // so however the separator and suffix are spaced, the lookup still lands.
    for (const input of fieldset.querySelectorAll('input')) {
      if (!(input instanceof HTMLInputElement)) continue;

      const parsed = this.#parseSizeValue(input.value);
      if (parsed && parsed.base === base.value.trim() && parsed.lift === lift.value.trim()) {
        this.#checkNativeOption(fieldset, input.value);
        return;
      }
    }

    console.warn(`[riser-configurator] no size matches "${base.value}" + "${lift.value}"`);
  }

  /** Pulls the current variant back into the split cards. */
  #syncSplitFromVariant = () => {
    const fieldset = this.#sizeFieldset;
    if (!fieldset) return;

    const current = fieldset.querySelector('input:checked');
    if (!(current instanceof HTMLInputElement)) return;

    const parsed = this.#parseSizeValue(current.value);
    if (!parsed) return;

    for (const input of this.querySelectorAll('[data-riser-size-part]')) {
      if (!(input instanceof HTMLInputElement)) continue;
      const wanted = input.dataset.riserSizePart === 'base' ? parsed.base : parsed.lift;
      input.checked = input.value.trim() === wanted;
    }
  };

  /** Finds the native fieldset for a variant option by name. */
  #findOptionFieldset(optionName) {
    const fieldset = this.querySelector(`[data-option-id$="-${optionName.toLowerCase()}"]`);
    return fieldset instanceof HTMLElement ? fieldset : null;
  }

  /** Checks a native option input and lets the variant picker react. */
  #checkNativeOption(fieldset, value) {
    const target = fieldset.querySelector(`input[value="${CSS.escape(value)}"]`);
    if (!(target instanceof HTMLInputElement)) {
      console.warn(`[riser-configurator] no variant option matches "${value}"`);
      return;
    }

    if (target.checked) return;

    target.checked = true;
    target.dispatchEvent(new Event('change', { bubbles: true }));
  }

  /**
   * Swaps the plain Shape button list for the illustrated cards above it.
   */
  #setupShapeDriver() {
    const optionName = this.dataset.shapeOption;
    if (!optionName) return;

    const fieldset = this.#findOptionFieldset(optionName);
    if (!fieldset) return;

    this.#shapeFieldset = fieldset;
    fieldset.style.display = 'none';

    this.#syncShapeFromVariant();
    document.addEventListener(ThemeEvents.variantUpdate, this.#syncShapeFromVariant);
  }

  #syncShapeFromVariant = () => {
    const fieldset = this.#shapeFieldset;
    if (!fieldset) return;

    // Selecting the custom card sets a placeholder Shape, which fires a
    // variant update. Without this guard that update would immediately
    // deselect the custom card the shopper just picked.
    if (this.querySelector('[data-riser-custom-shape]:checked')) return;

    const current = fieldset.querySelector('input:checked');
    if (!(current instanceof HTMLInputElement)) return;

    for (const input of this.querySelectorAll('[data-riser-option-part="shape"]')) {
      if (input instanceof HTMLInputElement) input.checked = input.value === current.value;
    }
  };

  /**
   * The real file input is visually hidden, so the chosen filename has to be
   * echoed next to the button that stands in for it.
   */
  #showFileName(input) {
    const output = this.querySelector('[data-riser-file-name]');
    if (!(output instanceof HTMLElement)) return;

    const names = Array.from(input.files ?? []).map((file) => file.name);
    output.textContent = names.length ? names.join(', ') : output.dataset.emptyLabel ?? '';
  }

  /**
   * Nudges toward extra strength when the chosen furniture is on the heavy
   * side. Hidden again once they tick the box, so it stops nagging.
   */
  #suggestStrength(value) {
    const note = this.querySelector('[data-riser-strength-suggestion]');
    if (!(note instanceof HTMLElement)) return;

    const heavy = (this.dataset.strengthTypes ?? '')
      .split('\n')
      .map((line) => line.trim().toLowerCase())
      .filter(Boolean);

    const checkbox = this.querySelector('#riser-strength');
    const alreadyAdded = checkbox instanceof HTMLInputElement && checkbox.checked;

    note.hidden = alreadyAdded || !heavy.includes(value.trim().toLowerCase());
  }

  #onChange = (/** @type {Event} */ event) => {
    const target = event.target;

    if (target instanceof HTMLSelectElement && target.hasAttribute('data-riser-furniture-type')) {
      this.#suggestStrength(target.value);
    }

    if (target instanceof HTMLInputElement && target.type === 'file') {
      this.#showFileName(target);
    }
    if (target instanceof HTMLInputElement && target.dataset.riserSizePart) {
      this.#applySizeSplit();
    }

    if (target instanceof HTMLInputElement && target.dataset.riserOptionPart === 'shape' && this.#shapeFieldset) {
      // A custom shape has no variant of its own, so the line keeps a real
      // Shape value as a placeholder and the description carries the detail.
      const value =
        target.dataset.riserCustomShape === 'true' ? this.dataset.customShapeBase : target.value;

      if (value) this.#checkNativeOption(this.#shapeFieldset, value);
    }

    if (target instanceof HTMLInputElement && target.id === 'riser-strength') {
      const type = this.querySelector('[data-riser-furniture-type]');
      if (type instanceof HTMLSelectElement) this.#suggestStrength(type.value);
    }

    if (target instanceof HTMLInputElement && target.id === 'riser-custom-color') {
      this.#syncColorLock(target.checked);
    }

    // The native color swatches live in their own variant-picker block, so a
    // shopper can otherwise end up with a listed color visibly selected *and*
    // "I want a color that isn't listed" checked at the same time. Picking a
    // real swatch means they don't want a custom one after all.
    if (target instanceof HTMLInputElement && target.type === 'radio' && this.#colorSlot?.contains(target)) {
      const customColor = this.querySelector('#riser-custom-color');
      if (customColor instanceof HTMLInputElement && customColor.checked) {
        customColor.checked = false;
        this.#syncColorLock(false);
      }
    }

    this.#syncReveals();
    this.#updateEstimate();
  };

  get #colorSlot() {
    return this.querySelector('.riser-step__slot');
  }

  /**
   * While "custom color" is checked, locks the native swatches so the
   * shopper can't also leave a listed color looking selected — picking a
   * swatch instead (see #onChange) un-checks custom color rather than the
   * other way around, since the variant underneath always needs some real
   * color selected regardless of which one is showing.
   * @param {boolean} locked
   */
  #syncColorLock(locked) {
    const slot = this.#colorSlot;
    if (!slot) return;

    for (const radio of slot.querySelectorAll('input[type="radio"]')) {
      if (radio instanceof HTMLInputElement) radio.disabled = locked;
    }
    slot.classList.toggle('riser-step__slot--locked', locked);
  }

  #onInput = (/** @type {Event} */ event) => {
    const target = event.target;
    if (target instanceof HTMLTextAreaElement && target.hasAttribute('data-riser-counter')) {
      const { notesCount } = this.refs;
      if (notesCount) notesCount.textContent = String(target.value.length);
    }
  };

  /**
   * Shows or hides the fields tied to a checkbox, and disables hidden inputs
   * so they never reach the cart as empty properties.
   */
  #syncReveals() {
    for (const toggle of this.querySelectorAll('[data-riser-toggle]')) {
      if (!(toggle instanceof HTMLInputElement)) continue;

      const reveal = this.querySelector(`[data-riser-reveal="${toggle.dataset.riserToggle}"]`);
      if (!(reveal instanceof HTMLElement)) continue;

      reveal.hidden = !toggle.checked;

      for (const field of reveal.querySelectorAll('input, select, textarea')) {
        if (field instanceof HTMLInputElement || field instanceof HTMLSelectElement || field instanceof HTMLTextAreaElement) {
          field.disabled = !toggle.checked;
          if (toggle.checked) {
            field.required = true;
          } else {
            field.required = false;
            field.value = '';
          }
        }
      }
    }
  }

  /**
   * Total surcharge in dollars across every selected card and checked add-on,
   * plus a human-readable list of what those surcharges are for.
   * @returns {{ dollars: number, covers: string[] }}
   */
  #collectFees() {
    let dollars = 0;
    const covers = [];
    const items = [];

    for (const input of this.querySelectorAll('input[data-riser-fee]')) {
      if (!(input instanceof HTMLInputElement) || !input.checked) continue;

      const fee = Number(input.dataset.riserFee ?? 0) || 0;
      if (fee <= 0) continue;

      dollars += fee;
      items.push({ label: this.#feeLabel(input), dollars: fee });

      if (input.type === 'radio') {
        const group = input.closest('.riser-step')?.querySelector('.riser-step__title')?.textContent?.trim();
        covers.push(group ? `${group}: ${input.value}` : input.value);
      } else {
        // Checkbox add-ons read better as their own label than as their step's.
        const label = input.closest('.riser-addon')?.querySelector('.riser-addon__text')?.cloneNode(true);
        label?.querySelector('.riser-addon__fee')?.remove();
        covers.push(label?.textContent?.trim() || 'Upgrade');
      }
    }

    return { dollars, covers, items };
  }

  #quantity() {
    const input = this.#form?.querySelector('[name="quantity"]');
    if (input instanceof HTMLInputElement) {
      return Math.max(1, Number(input.value) || 1);
    }
    return 1;
  }

  #money(cents) {
    const currency = this.dataset.currency || 'USD';
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency',
        currency,
      }).format(cents / 100);
    } catch {
      return `$${(cents / 100).toFixed(2)}`;
    }
  }

  /** The step heading a control sits under. */
  #stepTitle(el) {
    return el.closest('.riser-step')?.querySelector('.riser-step__title')?.textContent?.trim() ?? '';
  }

  /** A readable name for a fee-bearing control. */
  #feeLabel(input) {
    if (input.type === 'radio') return input.value;
    const label = input.closest('.riser-addon, .riser-shape')?.querySelector('.riser-addon__text')?.cloneNode(true);
    label?.querySelector('.riser-addon__fee')?.remove();
    return label?.textContent?.trim() || this.#stepTitle(input) || 'Upgrade';
  }

  /** Every choice made so far, in the order the steps appear. */
  #collectChoices() {
    const out = [];
    const add = (label, value) => {
      if (label && value) out.push({ label, value });
    };

    for (const input of this.querySelectorAll('[data-riser-size-part]:checked')) {
      if (input instanceof HTMLInputElement) add(this.#stepTitle(input), input.value);
    }

    // Variant options still shown natively, such as Color.
    for (const fieldset of this.querySelectorAll('.variant-option')) {
      if (!(fieldset instanceof HTMLElement) || fieldset.style.display === 'none') continue;
      const name = fieldset.querySelector('legend')?.childNodes[0]?.textContent?.trim();
      const checked = fieldset.querySelector('input:checked');
      if (name && checked instanceof HTMLInputElement) add(name, checked.value);
    }

    const shape = this.querySelector('[data-riser-option-part="shape"]:checked');
    if (shape instanceof HTMLInputElement) add(this.#stepTitle(shape), shape.value);

    for (const select of this.querySelectorAll('select[name^="properties"]')) {
      if (select instanceof HTMLSelectElement && select.value) add(this.#stepTitle(select), select.value);
    }

    return out;
  }

  /**
   * Mirrors the current build into every summary surface — the side panel and
   * the mobile bar both carry `data-riser-summary`, so they stay in step
   * without a second calculation.
   */
  #updateSummaryPanel(eachCents, qty, feeItems) {
    const choices = this.#collectChoices();
    for (const panel of document.querySelectorAll('[data-riser-summary]')) {
      this.#fillSummary(panel, choices, eachCents, qty, feeItems);
    }
  }

  /** @param {Element} panel */
  #fillSummary(panel, choices, eachCents, qty, feeItems) {
    if (!(panel instanceof HTMLElement)) return;
    const list = panel.querySelector('[data-riser-summary-list]');
    const empty = panel.querySelector('[data-riser-summary-empty]');

    if (list instanceof HTMLElement) {
      list.replaceChildren(
        ...choices.map(({ label, value }) => {
          const row = document.createElement('div');
          const dt = document.createElement('dt');
          dt.textContent = label;
          const dd = document.createElement('dd');
          dd.textContent = value;
          row.append(dt, dd);
          return row;
        })
      );
      list.hidden = choices.length === 0;
    }
    if (empty instanceof HTMLElement) empty.hidden = choices.length > 0;

    const fees = panel.querySelector('[data-riser-summary-fees]');
    if (fees instanceof HTMLElement) {
      fees.replaceChildren(
        ...feeItems.map(({ label, dollars }) => {
          const row = document.createElement('div');
          const name = document.createElement('span');
          name.textContent = label;
          const amount = document.createElement('span');
          amount.textContent = this.#money(Math.round(dollars * 100));
          row.append(name, amount);
          return row;
        })
      );
      fees.hidden = feeItems.length === 0;
    }

    const base = panel.querySelector('[data-riser-summary-base]');
    if (base instanceof HTMLElement) base.textContent = this.#money(this.#basePriceCents);

    const total = panel.querySelector('[data-riser-summary-total]');
    if (total instanceof HTMLElement) {
      const next = this.#money(eachCents * qty);
      const changed = total.textContent?.trim() !== next;
      total.textContent = next;
      // On the bar the number is the only thing that moves, so give it a beat
      // of motion and say it once for screen readers.
      if (changed && panel.hasAttribute('data-riser-mobile-bar')) {
        total.classList.remove('riser-mobile-total__value--bumped');
        void total.offsetWidth;
        total.classList.add('riser-mobile-total__value--bumped');
        const announce = panel.querySelector('[data-riser-summary-announce]');
        if (announce instanceof HTMLElement) announce.textContent = `Total ${next}`;
      }
    }

    const each = panel.querySelector('[data-riser-summary-each]');
    if (each instanceof HTMLElement) {
      each.textContent = qty > 1 ? `${this.#money(eachCents)} each × ${qty}` : '';
      each.hidden = qty <= 1;
    }

    // One-line recap for the mobile bar, where there is no room for the list.
    const recap = panel.querySelector('[data-riser-summary-recap]');
    if (recap instanceof HTMLElement) {
      recap.textContent = choices
        .slice(0, 3)
        .map(({ value }) => value)
        .join(' · ');
    }
  }

  #updateEstimate() {
    const { dollars, covers, items } = this.#collectFees();
    const qty = this.#quantity();
    const eachCents = this.#basePriceCents + Math.round(dollars * 100);

    this.#updateSummaryPanel(eachCents, qty, items);

    const { estimateTotal, estimateBreakdown } = this.refs;
    if (!estimateTotal) return;

    estimateTotal.textContent =
      qty > 1 ? `${this.#money(eachCents * qty)}` : this.#money(eachCents);

    if (!estimateBreakdown) return;

    const parts = [];
    if (qty > 1) parts.push(`${this.#money(eachCents)} each × ${qty}`);
    if (covers.length) parts.push(`Includes ${this.#money(Math.round(dollars * 100))} for ${covers.join(', ')}`);

    estimateBreakdown.textContent = parts.join(' · ');
    estimateBreakdown.hidden = parts.length === 0;
  }

  #onVariantUpdate = (/** @type {Event} */ event) => {
    const price = /** @type {any} */ (event).detail?.resource?.price;
    if (typeof price === 'number') {
      this.#basePriceCents = price;
      this.#updateEstimate();
    }

    // The native variant picker swaps in a freshly rendered set of swatch
    // inputs on every variant change (to update price/availability), which
    // have no idea custom color was checked — their `disabled` never carried
    // over. Re-apply the lock to whatever swatches exist now, not just the
    // ones #syncColorLock saw when the checkbox itself last changed.
    const customColor = this.querySelector('#riser-custom-color');
    if (customColor instanceof HTMLInputElement) this.#syncColorLock(customColor.checked);
  };

  /**
   * Records the fee owed for this add-to-cart. The fee line itself is added
   * once the main item is in the cart, so a failed add never leaves an
   * orphaned fee behind.
   */
  #onSubmit = (/** @type {Event} */ event) => {
    // This runs in the capture phase ahead of the theme's own submit handler,
    // so anything unexpected in here must never be able to block a sale.
    try {
      this.#guardSubmit(event);
    } catch (error) {
      console.error('[riser-configurator] submit guard failed, letting the form through', error);
    }
  };

  #guardSubmit(/** @type {Event} */ event) {
    this.#pendingFeeQty = 0;
    this.#pendingFeeCovers = '';
    this.#pendingRiserGroup = '';

    const { dollars, covers } = this.#collectFees();
    if (dollars <= 0) return;

    const feeVariantId = this.dataset.feeVariantId;
    const unitCents = Number(this.dataset.feeUnitCents ?? 0) || 0;

    // No way to charge the upgrades: refuse the sale instead of silently
    // handing them over for free.
    if (!feeVariantId || unitCents <= 0) {
      event.preventDefault();
      event.stopImmediatePropagation();
      this.#showError();
      console.error('[riser-configurator] fee product is not configured; add to cart was blocked');
      return;
    }

    this.#hideError();
    this.#pendingFeeQty = Math.round((dollars * 100) / unitCents) * this.#quantity();
    this.#pendingFeeCovers = covers.join(', ');
    this.#pendingMainVariantId = Number(this.#form?.querySelector('[name="id"]')?.value) || null;
    this.#pendingMainQty = this.#quantity();
    // Read rather than regenerate: the submit button's own click listener
    // already stamped the form (see connectedCallback) by the time this runs,
    // and #addFee needs the exact same token that's actually in the form.
    const riserGroupField = /** @type {HTMLInputElement | null} */ (
      this.#form?.querySelector('input[name="properties[_riser_group]"]')
    );
    this.#pendingRiserGroup = riserGroupField?.value ?? '';
  }

  /**
   * Stamps a fresh one-time token onto the main item's own add-to-cart
   * request as a hidden (underscore-prefixed, so it never shows in the cart
   * or checkout) property. Called from the submit button's click listener
   * (see connectedCallback), not from the submit handler itself — see the
   * comment there for why.
   * @returns {string}
   */
  #tagFormWithRiserGroup() {
    if (!this.#form) return '';

    const group =
      (window.crypto?.randomUUID && window.crypto.randomUUID()) ||
      `${Date.now()}-${Math.random().toString(36).slice(2)}`;

    let field = this.#form.querySelector('input[name="properties[_riser_group]"]');
    if (!(field instanceof HTMLInputElement)) {
      field = document.createElement('input');
      field.type = 'hidden';
      field.name = 'properties[_riser_group]';
      this.#form.appendChild(field);
    }
    field.value = group;

    return group;
  }

  #onCartUpdate = (/** @type {Event} */ event) => {
    if (this.#applyingFee || this.#pendingFeeQty <= 0) return;
    if (/** @type {any} */ (event).detail?.data?.didError) {
      this.#pendingFeeQty = 0;
      return;
    }

    const quantity = this.#pendingFeeQty;
    const covers = this.#pendingFeeCovers;
    const mainVariantId = this.#pendingMainVariantId;
    const mainQty = this.#pendingMainQty;
    const riserGroup = this.#pendingRiserGroup;
    this.#pendingFeeQty = 0;
    this.#pendingFeeCovers = '';
    this.#pendingMainVariantId = null;
    this.#pendingMainQty = 0;
    this.#pendingRiserGroup = '';

    this.#addFee(quantity, covers, mainVariantId, mainQty, riserGroup);
  };

  async #addFee(quantity, covers, mainVariantId, mainQty, riserGroup) {
    const id = this.dataset.feeVariantId;
    if (!id) return;

    this.#applyingFee = true;

    try {
      const properties = {};
      if (covers) properties.Covers = covers;
      // Same hidden token as the main item (see #tagFormWithRiserGroup), so the
      // cart can later match this fee line back to the riser it's charging for.
      if (riserGroup) properties._riser_group = riserGroup;

      const response = await fetch(`${this.#root()}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [
            {
              id: Number(id),
              quantity,
              properties,
            },
          ],
        }),
      });

      if (!response.ok) throw new Error(`Fee add failed with ${response.status}`);

      document.dispatchEvent(
        new CartUpdateEvent({}, 'riser-configurator', { source: 'riser-configurator' })
      );
    } catch (error) {
      // The upgrades could not be charged, so the item must not stay in the
      // cart at a price that leaves them out.
      console.error('[riser-configurator] could not add the upgrade fee to the cart', error);
      await this.#rollBackMainItem(mainVariantId, mainQty);
      this.#showError();
    } finally {
      this.#applyingFee = false;
    }
  }

  #root() {
    return window.Shopify?.routes?.root ?? '/';
  }

  /**
   * Takes back the quantity that was just added, leaving anything the shopper
   * had in the cart beforehand untouched.
   */
  async #rollBackMainItem(variantId, quantity) {
    if (!variantId || quantity <= 0) return;

    try {
      const cartResponse = await fetch(`${this.#root()}cart.js`, { headers: { Accept: 'application/json' } });
      if (!cartResponse.ok) throw new Error(`Cart read failed with ${cartResponse.status}`);

      const cart = await cartResponse.json();
      const line = [...cart.items].reverse().find((item) => item.variant_id === variantId);
      if (!line) return;

      const response = await fetch(`${this.#root()}cart/change.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: line.key, quantity: Math.max(0, line.quantity - quantity) }),
      });

      if (!response.ok) throw new Error(`Rollback failed with ${response.status}`);

      document.dispatchEvent(
        new CartUpdateEvent({}, 'riser-configurator', { source: 'riser-configurator' })
      );
    } catch (error) {
      console.error('[riser-configurator] could not roll the item back out of the cart', error);
    }
  }

  #showError() {
    const error = document.querySelector('[data-riser-error]');
    if (error instanceof HTMLElement) {
      error.hidden = false;
      error.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }

  #hideError() {
    const error = document.querySelector('[data-riser-error]');
    if (error instanceof HTMLElement) error.hidden = true;
  }
}

if (!customElements.get('riser-configurator-component')) {
  customElements.define('riser-configurator-component', RiserConfigurator);
}
